import { Lock } from 'lucide-react'
import { Tooltip } from '@/components/ui/Tooltip'
import { useScopeStore } from '../store'
import type { ResellerConstraint } from '../utils/resellerConstraints'

/**
 * Hook to check if a specific field is constrained by the parent reseller scope.
 * Returns the constraint if locked, or null.
 *
 * Usage: const constraint = useResellerConstraint('amlConfig', 'vendor')
 */
export function useResellerConstraint(section: string, field: string): ResellerConstraint | null {
  const { resellerConstraints } = useScopeStore()
  if (!resellerConstraints) return null
  return resellerConstraints[`${section}.${field}`] || null
}

/**
 * Small lock icon with tooltip, shown next to field labels when a reseller constraint applies.
 */
export function ResellerLockBadge({ section, field }: { section: string; field: string }) {
  const constraint = useResellerConstraint(section, field)
  const { parentCustomerName } = useScopeStore()
  if (!constraint) return null

  const tip = constraint.type === 'locked'
    ? `Locked by reseller (${parentCustomerName || 'Parent'})`
    : `Restricted by reseller (${parentCustomerName || 'Parent'})`

  return (
    <Tooltip content={tip}>
      <span className="inline-flex items-center ml-1.5">
        <Lock size={12} className="text-blue-400" />
      </span>
    </Tooltip>
  )
}

/**
 * Returns true if the field is locked by a reseller constraint (should be disabled).
 */
export function useIsResellerLocked(section: string, field: string): boolean {
  const constraint = useResellerConstraint(section, field)
  return constraint?.type === 'locked'
}

/**
 * Returns true if the scope has a reseller parent linked (entire imported tabs should be disabled).
 */
export function useHasResellerParent(): boolean {
  const { resellerConstraints } = useScopeStore()
  return resellerConstraints !== null
}

/**
 * Banner displayed at the top of tabs whose config is inherited from the parent reseller scope.
 */
export function ResellerInheritedBanner() {
  const { parentCustomerName } = useScopeStore()
  const hasParent = useHasResellerParent()
  if (!hasParent) return null

  return (
    <div className="flex items-center gap-2 px-4 py-2 bg-blue-400/10 border border-blue-400/30 rounded-lg text-sm text-blue-300 mb-4">
      <Lock size={14} className="shrink-0" />
      <span>
        Inherited from reseller <strong>{parentCustomerName || 'Parent'}</strong> — this tab is read-only.
      </span>
    </div>
  )
}
